// services/stockApi.js
//
// «المخزون» — the ops stock page API (tasks/stock.md) on the shared secureApi instance; the auth header
// is attached automatically. Each call returns the parsed response body.
//
// Writes pass `preserveBackendError` so the page can read the backend's stable `code` (STOCK_xxx) and
// translate it (config/stockLabels.js), instead of secureApi's generic text.

import { secureApi } from './secureApi';
import { newIdempotencyKey } from './conversationApi';

const BASE = '/api/v1/stock';
const KEEP_CODE = { preserveBackendError: true };

const stockApi = {
  /** Unit codes, period presets and the overdue threshold — the forms hard-code none of them. */
  getOptions: async () => (await secureApi.get(`${BASE}/options`)).data,
  /** Who stock can be handed to — the same filter the take-out accepts. */
  getTakers: async () => (await secureApi.get(`${BASE}/takers`)).data,

  getStores: async () => (await secureApi.get(`${BASE}/stores`)).data,
  createStore: async ({ name }) => (await secureApi.post(`${BASE}/stores`, { name }, KEEP_CODE)).data,

  /** Every item's quantity, last count and flags, plus the banner counts — all decided by the server. */
  getSummary: async ({ storeId, includeArchived }) => (await secureApi.get(`${BASE}/stores/${storeId}/summary`, {
    params: includeArchived ? { includeArchived: 'true' } : {}
  })).data,
  /** The per-person check; the period is resolved in Riyadh on the server. */
  getPeople: async ({ storeId, period }) => (await secureApi.get(`${BASE}/stores/${storeId}/people`, { params: { period } })).data,
  getHistory: async ({ storeId, itemId }) => (await secureApi.get(`${BASE}/stores/${storeId}/items/${itemId}/history`)).data,

  /** `{ storeId, nameAr, nameEn, unit, lowStockLevel, openingQuantity, idempotencyKey }` */
  createItem: async ({ storeId, ...body }) => (await secureApi.post(`${BASE}/stores/${storeId}/items`, body, KEEP_CODE)).data,
  /** One form submit — `{ storeId, lines, takenBy?, note?, idempotencyKey }`. */
  recordPurchase: async ({ storeId, ...body }) => (await secureApi.post(`${BASE}/stores/${storeId}/purchases`, body, KEEP_CODE)).data,
  recordTakeOut: async ({ storeId, ...body }) => (await secureApi.post(`${BASE}/stores/${storeId}/take-outs`, body, KEEP_CODE)).data,
  recordRecount: async ({ storeId, ...body }) => (await secureApi.post(`${BASE}/stores/${storeId}/recounts`, body, KEEP_CODE)).data,
  reverseEntry: async ({ entryId, note }) => (await secureApi.post(`${BASE}/entries/${entryId}/reverse`, { note }, KEEP_CODE)).data,

  /** Names and the low-stock level. */
  updateItem: async ({ itemId, changes }) => (await secureApi.patch(`${BASE}/items/${itemId}`, changes, KEEP_CODE)).data,
  archiveItem: async ({ itemId }) => (await secureApi.post(`${BASE}/items/${itemId}/archive`, {}, KEEP_CODE)).data,
  restoreItem: async ({ itemId }) => (await secureApi.post(`${BASE}/items/${itemId}/restore`, {}, KEEP_CODE)).data,
  /** Set or replace the item's one photo (multipart field `photo`) — the inbox photo-reply call shape. */
  uploadPhoto: async ({ itemId, file }) => {
    const form = new FormData();
    form.append('photo', file);
    return (await secureApi.post(`${BASE}/items/${itemId}/photo`, form, {
      ...KEEP_CODE,
      timeout: 60000,
      headers: { 'Content-Type': undefined }
    })).data;
  },
};

// One key per form opening (D11): a double tap or a retry of the same submit is recorded once.
export { newIdempotencyKey };
export default stockApi;
